import useDebouncedValue from "../hooks/useDebouncedValue";
import { useStateParams } from "../hooks/useStateParams";
import { isSortOrder, SortOrder } from "./common/models";
import { ApiPaginatedData } from "./common/paginatedData";
import useAuthenticatedFetch from "./common/useAuthenticatedFetch";
import { useAuthenticatedApiRequest } from "./common/useAuthenticatedFetcher";
import {
    ActivityData,
    ActivityDataDetail,
    ActivityDataOrderBy,
    ActivityDataRawMaterial,
    ActivityDataUnit,
    AddActivityDataDto,
    isActivityDataOrderBy,
} from "./models/ActivityData";
import { ApiResponse } from "./models/ApiResponse";

export const useAddActivityData = <T extends AddActivityDataDto>(id: number) =>
    useAuthenticatedApiRequest<T, number>(
        "POST",
        `api/data-sources/${id}/activity-data`
    );

export type UsePaginatedActivityDataReturn = {
    activityDataList: ActivityData[];
    pageNumber: number;
    pageSize: number;
    orderBy: ActivityDataOrderBy;
    sortOrder: SortOrder;
    scopes: number[];
    locationIds: number[];
    dataSourceIds: number[];
    currentEmissionsFactorStatus: string | null;
    search: string;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    isLoading: boolean;
    setPageNumber: (
        pageNumber: ((currentValue: number) => number) | number
    ) => void;
    setPageSize: (
        pageSize: ((currentValue: number) => number) | number
    ) => void;
    setScopes: (
        scopes: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setOrderBy: (
        orderBy:
            | ((currentValue: ActivityDataOrderBy) => ActivityDataOrderBy)
            | ActivityDataOrderBy
    ) => void;
    setSortOrder: (
        sortOrder: ((currentValue: SortOrder) => SortOrder) | SortOrder
    ) => void;
    setLocationIds: (
        locations: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setDataSourceIds: (
        dataSourceIds: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setCurrentEmissionsFactorStatus: (
        currentEmissionsFactorStatus:
            | ((currentValue: string | null) => string | null)
            | string
            | null
    ) => void;
    setSearch: (
        searchQuery: ((currentValue: string) => string) | string
    ) => void;
};

export type ScopeFilter = "1" | "2" | "3" | "1,2,3";
const defaultPageIndex = 0;
const defaultPageSize = 15;
export const defaultScopeValue: ScopeFilter = "1,2,3";

export const usePaginatedActivityData = (
    fromActivityDate: string,
    toActivityDate: string
): UsePaginatedActivityDataReturn => {
    const queryParamSuffix = "Ad";
    const [pageNumber, setPageNumber] = useStateParams<number>(
        defaultPageIndex,
        "page" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
    );
    const [pageSize, setPageSize] = useStateParams<number>(
        defaultPageSize,
        "pageSize" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
    );
    const [scopes, setScopes] = useStateParams<number[]>(
        [],
        "scope" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [orderBy, setOrderBy] = useStateParams<ActivityDataOrderBy>(
        "ActivityDate",
        "orderBy" + queryParamSuffix,
        (s) => s,
        (s) => (isActivityDataOrderBy(s) ? s : "ActivityDate")
    );
    const [sortOrder, setSortOrder] = useStateParams<SortOrder>(
        "asc",
        "sortOrder" + queryParamSuffix,
        (s) => s,
        (s) => (isSortOrder(s) ? s : "asc")
    );
    const [locationIds, setLocationIds] = useStateParams<number[]>(
        [],
        "locationId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [dataSourceIds, setDataSourceIds] = useStateParams<number[]>(
        [],
        "dataSourceId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [currentEmissionsFactorStatus, setCurrentEmissionsFactorStatus] =
        useStateParams<string | null>(
            null,
            "currentEmissionsFactorStatus" + queryParamSuffix,
            (s) => (s ? s : ""),
            (s) => s
        );
    const [search, setSearch] = useStateParams<string>(
        "",
        "search" + queryParamSuffix,
        (s) => s,
        (s) => s
    );
    const debouncedSearchValue = useDebouncedValue(search, 500);

    const locationIdsQueryParam =
        locationIds.length > 0 ? `&locationIds=${locationIds}` : "";
    const dataSourceIdsQueryParam =
        dataSourceIds.length > 0 ? `&dataSourceIds=${dataSourceIds}` : "";
    const scopesQueryParam = scopes.length > 0 ? `&scopes=${scopes}` : "";
    const currentEmissionsFactorStatusQueryParam = currentEmissionsFactorStatus
        ? `&emissionsFactorStatus=${currentEmissionsFactorStatus}`
        : "";
    const searchQueryParam =
        search !== "" ? `&searchTerm=${debouncedSearchValue}` : "";

    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ApiPaginatedData<ActivityData>>
    >(
        `api/activity-data?pageIndex=${pageNumber}` +
            `&pageSize=${pageSize}` +
            `&fromActivityDate=${fromActivityDate}` +
            `&toActivityDate=${toActivityDate}` +
            `&orderBy=${orderBy === "ActivityDate" ? "ActivityStartDate" : orderBy}` +
            `&sortOrder=${sortOrder}` +
            `${locationIdsQueryParam}` +
            `${dataSourceIdsQueryParam}` +
            `${scopesQueryParam}` +
            `${searchQueryParam}` +
            `${currentEmissionsFactorStatusQueryParam}`
    );

    return {
        activityDataList: response?.content.items ?? [],
        isLoading,
        pageNumber: pageNumber,
        pageSize,
        orderBy,
        sortOrder,
        scopes,
        locationIds,
        dataSourceIds,
        currentEmissionsFactorStatus,
        search,
        totalCount: response?.content.totalCount ?? 0,
        totalPages: response?.content.totalPages ?? 0,
        hasPreviousPage: response?.content.hasPreviousPage ?? false,
        hasNextPage: response?.content.hasNextPage ?? false,
        setPageNumber,
        setPageSize,
        setScopes,
        setOrderBy,
        setSortOrder,
        setLocationIds,
        setDataSourceIds,
        setCurrentEmissionsFactorStatus,
        setSearch,
    };
};

export const useActivityDataDetail = (id: number) => {
    const { response, isLoading, refetch } = useAuthenticatedFetch<
        ApiResponse<ActivityDataDetail>
    >(`api/activity-data/${id}`);

    return {
        activityData: response?.content,
        isLoading,
        refetch,
    };
};

export const useActivityDataUnits = () => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ActivityDataUnit[]>
    >("api/activity-data-units");

    return {
        activityDataUnits: response?.content ?? [],
        isLoading,
    };
};

export const useActivityDataRawMaterial = () => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ActivityDataRawMaterial[]>
    >("api/activity-data-raw-material");

    return {
        activityDataRawMaterials: response?.content ?? [],
        isLoading,
    };
};

export const useActivityDataReport = () =>
    useAuthenticatedApiRequest<
        void,
        Blob,
        { fromDate: string; toDate: string }
    >(
        "GET",
        ({ fromDate, toDate }) =>
            `api/activity-data-report?fromDate=${fromDate}&toDate=${toDate}`
    );
